import type { MedicalHistoryRecordPatch } from "./medicalHistoryRecordFixture";

/**
 * Patient-record patches for the Medical History per-item detail steps.
 *
 * Each entry carries only the fields its own flow writes and reads back — the
 * ones its `transform*StateToRequest` PATCHes and its section context hydrates
 * off the GET — in the shape the patient endpoint returns them. That is the
 * same rule `completeSectionFixture.ts` follows, and it keeps the fixture from
 * asserting anything about the record the product itself never touches.
 */
export const DETAIL_STEP_RECORD_FIXTURES: Record<
  string,
  MedicalHistoryRecordPatch
> = {
  "vaccine-details": {
    medicalHistory: {
      isVaccinated: true,
      vaccinations: [
        {
          id: "6b2f1d84-3c9a-4d17-8e55-0a1c7f9b2e31",
          type: "covid19",
          other: null,
          brand: "Comirnaty",
          dateReceived: "2023-11-02",
          notes: "Third dose. Sore arm for a day, nothing else.",
        },
        {
          id: "c47a9e05-2d61-4f38-91b4-8a0f3e6c5d72",
          type: "tetanus",
          other: null,
          brand: "Boostrix",
          dateReceived: "2019-05-21",
          notes: "Booster after a garden cut, given at the GP.",
        },
      ],
    },
  },

  "surgeries-details": {
    medicalHistory: {
      hasSurgeries: true,
      surgeries: [
        {
          id: "2a7d6f31-5c04-4e89-b1f2-6d8a0c3e9b47",
          surgery: "operative_laparoscopy",
          other: null,
          date: "2021-06-14",
          comments:
            "Endometriosis excision, day case. Recovery was uneventful.",
        },
        {
          id: "84be1c72-9f35-4a60-8d27-3e5c1b0a7d69",
          surgery: "other",
          other: "Appendectomy",
          date: "2016-02-09",
          comments: "Emergency appendectomy, keyhole. No complications since.",
        },
      ],
    },
  },

  "contraception-details": {
    obGynHistory: {
      hasContraceptionHistory: true,
      contraceptions: [
        {
          id: "0f1a6c53-8b27-4d90-a6e1-5c93d7b40f26",
          method: "birth_control_pill",
          other: null,
          contraceptionName: "Yasmin",
          startDate: "2016-04-01",
          endDate: "2021-09-15",
          daysTillPeriodAfterStopping: 42,
          notes: "Stopped to start trying to conceive.",
        },
        {
          id: "7d5b2e18-4a63-4c05-9f72-1e8d0a4b6c39",
          method: "hormonal_iud",
          other: null,
          contraceptionName: "Mirena",
          startDate: "2021-10-01",
          endDate: "2023-06-30",
          daysTillPeriodAfterStopping: 28,
          notes: "Removed before the fertility work-up.",
        },
      ],
    },
  },

  "pregnancy-details": {
    obGynHistory: {
      hasBeenPregnant: true,
      pregnancies: [
        {
          id: "3c9e7f42-6d18-4b50-8a37-2f5c1b9e0d64",
          startDate: "2019-02-11",
          outcome: "live_birth",
          deliveryDate: "2019-11-20",
          deliveryType: "natural",
          withCurrentPartner: true,
          notes: "Full-term delivery, no complications.",
        },
        {
          id: "b81d0a37-5e24-4f69-93c1-6a7b2d8e5f10",
          startDate: "2022-05-03",
          outcome: "miscarriage",
          deliveryDate: null,
          deliveryType: null,
          withCurrentPartner: true,
          notes: "Miscarriage at nine weeks; follow-up scan clear.",
        },
      ],
    },
  },

  "exposure-details": {
    lifestyle: {
      hasExposures: true,
      exposures: [
        {
          id: "3a1f9c64-7d02-4b18-9e5a-2c7f4d61b083",
          exposure: "chemicals",
          frequency: "2-3_a_week",
          context: "Cleaning solvents in the lab I work in.",
          other: null,
        },
        {
          id: "b27c5e10-4d93-4f6a-8c11-90ad3e7f5c62",
          exposure: "x_rays",
          frequency: "less_1_a_month",
          context: "Dental and chest X-rays at the clinic.",
          other: null,
        },
      ],
    },
  },

  "substance-details": {
    lifestyle: {
      hasAddictions: true,
      addictions: [
        {
          id: "c94d7b35-6e28-4a10-b7f3-15d0a8e4c721",
          substance: "alcohol",
          frequency: "weekly_or_less",
          averageQuantity: 2,
          otherFrequency: null,
          startDate: "2015-06-01",
          stopDate: null,
          notes: "A couple of glasses of wine with dinner at the weekend.",
          other: null,
        },
        {
          id: "5e83f2a7-0b46-4c9d-8137-a2be6d4f90c8",
          substance: "cigarettes",
          frequency: "daily",
          averageQuantity: 5,
          otherFrequency: null,
          startDate: "2012-03-01",
          stopDate: "2021-09-01",
          notes: "Quit after starting fertility treatment.",
          other: null,
        },
      ],
    },
  },

  "disease-history-details": {
    testResults: [
      {
        id: "4d0c8b16-2e75-4a39-9b64-7f1a5c8e2d03",
        type: "imaging",
        imagingTestCategory: "mammogram",
        date: "2025-02-11",
        patientValue: "BI-RADS 2 — benign findings",
        notes: "Routine screening at the breast unit; repeat in 12 months.",
      },
      {
        id: "9a6e3f28-5b01-4d74-8c29-3e7b0d1a6f54",
        type: "imaging",
        imagingTestCategory: "mammogram",
        date: "2024-01-23",
        patientValue: "BI-RADS 1 — normal",
        notes: "Baseline mammogram before starting treatment.",
      },
      {
        id: "e15b7d40-8c62-4f93-a017-2d9c4b6e8a35",
        type: "clinical",
        clinicalTestCategory: "breast_examination",
        date: "2025-03-04",
        patientValue: "No palpable lump",
        notes: "Clinical breast exam at the annual gynae visit.",
      },
    ],
    medicalHistory: {
      hasBreastTestings: true,
      hasEndocrineDiseases: true,
      hasAutoimmuneDiseases: true,
      hasCancerHistory: true,
      diseases: [
        {
          id: "1c7a4e93-6d05-4b28-9f31-8a2e5c0d7b64",
          category: "endocrine",
          disease: "pcos",
          diagnosisDate: "2019-06-02",
          treatmentDescription: "Metformin 500 mg twice daily",
        },
        {
          id: "6b3d1f82-9e47-4a05-8c16-2f7a4d9b0e53",
          category: "endocrine",
          disease: "other",
          other: "Hashimoto thyroiditis",
          diagnosisDate: "2021-09-14",
          treatmentDescription: "Levothyroxine 75 mcg daily",
        },
        {
          id: "a04f6c25-3b19-4e70-92d8-5c1b7e3a8f96",
          category: "autoimmune",
          disease: "lupus",
          diagnosisDate: "2022-11-30",
          treatmentDescription: "Hydroxychloroquine 200 mg daily",
        },
      ],
      cancers: [
        {
          id: "2f8b5a17-4c60-4d39-81e2-7a3d9c6b0f45",
          cancer: "Breast cancer",
          diagnosisDate: "2018-04-17",
          inRemission: "Yes, since 2020",
          treatments: [
            {
              id: "7e2c9d04-1a58-4b63-9f07-3d6b8a2e5c14",
              treatmentType: "surgery",
              startDate: "2018-05-02",
              endDate: "2018-05-02",
              notes: "Lumpectomy, left breast.",
            },
            {
              id: "c53a7f61-8d29-4e04-b912-6f0c4a8d3b27",
              treatmentType: "chemotherapy",
              startDate: "2018-06-11",
              endDate: "2018-11-20",
              notes: "Six cycles; tolerated well.",
            },
          ],
        },
        {
          id: "8d40b2e7-5f93-4c16-a728-1b6e9d3c0a58",
          cancer: "Thyroid cancer",
          diagnosisDate: "2023-02-08",
          inRemission: "Not yet — under surveillance",
          treatments: [
            {
              id: "f61e8c39-0b74-4a52-9d18-4c2a7b5e6d03",
              treatmentType: "radiation_therapy",
              startDate: "2023-03-15",
              endDate: "2023-04-26",
              notes: "Radioiodine ablation, single course.",
            },
          ],
        },
      ],
    },
  },

  "sexual-health-details": {
    obGynHistory: {
      sexualHealth: {
        hadPapSmear: true,
        papSmearResultAbnormal: true,
        hadPapSmearProcedures: true,
        papSmearProcedures: [
          {
            id: "0b9d4a71-6c38-4e25-8f10-5a7c2e9b3d46",
            procedure: "colposcopy",
            procedureDate: "2024-05-21",
            notes: "Follow-up after an abnormal smear; biopsy taken.",
          },
          {
            id: "d72f5b08-3e94-4a61-9c57-8b1d0a6f4e23",
            procedure: "cryotherapy",
            procedureDate: "2024-07-09",
            notes: "Treated the affected area; healed without complications.",
          },
        ],
        hadSTDs: true,
        stds: [
          {
            id: "5a3e0c82-7b16-4d49-8e05-2f9b6d1a7c34",
            stdType: "chlamydia",
            dateOfDiagnosis: "2021-08-14",
            treatment: "Doxycycline, 7-day course",
            notes: "Partner treated at the same time; test of cure negative.",
          },
          {
            id: "b46c1d59-0a82-4f37-93e6-7d5a8b2c0e19",
            stdType: "other",
            other: "Mycoplasma genitalium",
            dateOfDiagnosis: "2023-03-02",
            notes: "Diagnosed at the sexual-health clinic; resolved.",
          },
        ],
      },
    },
  },
};
